import { Box, Fade, Stack, Tab, Tabs } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { HelpAndSupportTab } from 'src/components/Assistant/assistant-help-tab';
import { LiveTranscriptDrawer } from 'src/components/Assistant/live-transcript/live-transcript-drawer';
import { SupportPoliciesTab } from 'src/components/Assistant/policies-tab/policies-tab';
import { SidekickTab } from 'src/components/Assistant/sidekick-tab/sidekick-tab';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useGlobalStore } from 'src/store';
import useNavigationStore, { type SidekickSubtab } from 'src/store/navigation';
import {
  sidekickPaneContentSx,
  sidekickPaneSx,
  sidekickSubtabsSx,
} from './sidekick-pane.styles';
import { useSidekickAccess } from './use-sidekick-access';

enum SidekickTabLabel {
  AiChat = 'AI Chat',
  Help = 'Help',
  Policies = 'Policies',
}

const SUBTAB_TO_LABEL: Record<SidekickSubtab, SidekickTabLabel> = {
  policies: SidekickTabLabel.Policies,
  'ai-chat': SidekickTabLabel.AiChat,
  help: SidekickTabLabel.Help,
};

export const SidekickPane = () => {
  const [tab, setTab] = useState(0);
  const [isLiveTranscriptVisible, setIsLiveTranscriptVisible] = useState(false);

  const currentPartnerId = useGlobalStore.use.selectedPartnerId();

  const {
    canViewPolicies,
    canManagePolicies,
    canUseAiChat,
    conversationPartnerId,
    selectedConversation,
  } = useSidekickAccess();

  const partnerIdForPolicies = currentPartnerId ?? PLATFORM_PARTNER_ID;

  const sidekickRequest = useNavigationStore((state) => state.sidekickRequest);
  const requestSidekick = useNavigationStore((state) => state.requestSidekick);
  const [focusPolicyId, setFocusPolicyId] = useState<string | null>(null);

  const sidekickTabs = useMemo(() => {
    const tabs: { label: SidekickTabLabel; content: React.ReactNode }[] = [];

    if (canViewPolicies || canManagePolicies) {
      tabs.push({
        label: SidekickTabLabel.Policies,
        content: (
          <SupportPoliciesTab
            fallbackPartnerId={partnerIdForPolicies}
            focusPolicyId={focusPolicyId}
            onFocusPolicyHandled={() => setFocusPolicyId(null)}
          />
        ),
      });
    }

    if (canUseAiChat) {
      tabs.push({
        label: SidekickTabLabel.AiChat,
        content: (
          <SidekickTab
            conversationSubject={selectedConversation?.subject}
            friendlyConversationId={selectedConversation?.friendlyId}
            conversationId={selectedConversation?.conversationId}
            partnerId={conversationPartnerId}
          />
        ),
      });
    }

    tabs.push({
      label: SidekickTabLabel.Help,
      content: <HelpAndSupportTab />,
    });
    return tabs;
  }, [
    canUseAiChat,
    canManagePolicies,
    canViewPolicies,
    partnerIdForPolicies,
    focusPolicyId,
    selectedConversation?.conversationId,
    selectedConversation?.subject,
    selectedConversation?.friendlyId,
    conversationPartnerId,
  ]);

  // a deep link names a subtab, so resolve it against this user's tabs, not an index
  useEffect(() => {
    if (!sidekickRequest) {
      return;
    }

    const wanted = SUBTAB_TO_LABEL[sidekickRequest.subtab];
    const index = sidekickTabs.findIndex((tabDef) => tabDef.label === wanted);
    if (index !== -1) {
      setTab(index);
    }
    if (sidekickRequest.policyId) {
      setFocusPolicyId(sidekickRequest.policyId);
    }
    requestSidekick(null);
  }, [sidekickRequest, sidekickTabs, requestSidekick]);

  const activeTab = tab < sidekickTabs.length ? tab : 0;
  const activeTabLabel = sidekickTabs[activeTab]?.label;
  const shouldShowLiveTranscription =
    activeTabLabel === SidekickTabLabel.Policies || activeTabLabel === SidekickTabLabel.AiChat;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => setTab(newValue);

  return (
    <Stack sx={sidekickPaneSx} data-testid="sidekick-pane">
      {sidekickTabs.length > 1 ? (
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={sidekickSubtabsSx}
        >
          {sidekickTabs.map(({ label }) => (
            <Tab key={label} label={label} disableRipple />
          ))}
        </Tabs>
      ) : null}

      <Box
        sx={sidekickPaneContentSx({
          shouldShowLiveTranscription,
          isLiveTranscriptVisible,
        })}
      >
        <TransitionGroup component={null}>
          <Fade key={activeTab} timeout={{ enter: 250, exit: 150 }} mountOnEnter unmountOnExit>
            <Stack height="100%">{sidekickTabs[activeTab]?.content}</Stack>
          </Fade>
        </TransitionGroup>
        <LiveTranscriptDrawer
          enabled={shouldShowLiveTranscription}
          onVisibilityChange={setIsLiveTranscriptVisible}
        />
      </Box>
    </Stack>
  );
};
